import { useCallback } from "react";
import { getCsrfHeader } from "../lib/csrf";
import { FEATURE_FLAGS, flagEnabled, useAuth } from "./useAuth";
import type { Integration } from "../types/integration";

// Where a customer lands when we can't start the OAuth flow for them.
export const integrationDetailPath = (slug: string): string =>
  `/dashboard/integrations/${encodeURIComponent(slug)}`;

// True when we can hand the customer straight to the provider's OAuth page.
// Everything else — API-key forms, guided wizards (Telegram), anything the
// catalog says isn't connectable — needs the detail page's own UI.
export const canStartOauth = (
  integration: Integration | undefined,
  connectEnabled: boolean,
): boolean =>
  connectEnabled &&
  integration !== undefined &&
  integration.configured &&
  integration.authType === "oauth2" &&
  integration.setupKind !== "guided";

export type ConnectStarter = (
  slug: string,
  integration: Integration | undefined,
) => Promise<void>;

// Starts a connect from anywhere outside the integration detail page (the
// onboarding hand-off, the locked-instance checklist).
//
// Always opens a NEW tab: these surfaces are lists, and a customer working
// through three apps must not lose the list to a same-tab navigation. The tab
// is opened synchronously inside the click gesture — opening it after the
// await would be popup-blocked — then pointed at either the provider's OAuth
// page or, for anything we can't start directly, the integration's detail
// page. A failed mint falls back to the detail page rather than a dead tab.
export const useConnectIntegration = (tenantId: string | null): ConnectStarter => {
  const { user } = useAuth();
  const connectEnabled = flagEnabled(user?.features, FEATURE_FLAGS.integrationsConnectEnabled);

  return useCallback(
    async (slug: string, integration: Integration | undefined): Promise<void> => {
      const tab = window.open("about:blank", "_blank");
      // Detach the opener so the provider's page can't reach back into us.
      if (tab) tab.opener = null;
      const fallback = (): void => {
        const url = integrationDetailPath(slug);
        if (tab) tab.location.replace(url);
        else window.open(url, "_blank", "noopener,noreferrer");
      };

      if (tenantId === null || !canStartOauth(integration, connectEnabled)) {
        fallback();
        return;
      }

      try {
        const res = await fetch(
          `/api/integrations/${encodeURIComponent(tenantId)}/${encodeURIComponent(slug)}/connect`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json", ...getCsrfHeader() },
            body: JSON.stringify({ alias: null }),
          },
        );
        if (!res.ok) throw new Error(String(res.status));
        const { redirectUrl } = (await res.json()) as { redirectUrl: string };
        if (tab) tab.location.replace(redirectUrl);
        else window.open(redirectUrl, "_blank", "noopener,noreferrer");
      } catch {
        // Mint failed (flag flipped, session expired, upstream blip) — send
        // them to the page that can explain it rather than leaving a blank tab.
        fallback();
      }
    },
    [connectEnabled, tenantId],
  );
};
